import { NumberField as NumberFieldPrimitive } from "@base-ui/react/number-field";
import { IconMinus, IconPlus, IconUpload, IconX } from "@tabler/icons-react";
import {
  type ComponentProps,
  type ReactNode,
  useId,
  useRef,
  useState,
} from "react";

import { Button } from "#app/components/ui/button";
import { Input } from "#app/components/ui/input";
import { cn } from "#app/utils/misc.tsx";

/**
 * The form controls antd covered that neither the shadcn registry nor the
 * prototype provides. Built in the same vocabulary as the rest of the new
 * design — rounded-full affordances, token colours, Base UI where it has a
 * primitive and the platform control where it does not.
 *
 * `DateField` / `TimeField` / `ColorField` deliberately use native inputs. A
 * bespoke calendar and clock would be a lot of surface to own for two call
 * sites (the two dynamic workflow-input forms), and the native controls are
 * keyboard- and locale-correct for free.
 */

/** antd `<InputNumber>`. Base UI's NumberField gives us the scrub and steppers. */
export function NumberField({
  value,
  onValueChange,
  disabled,
  min,
  max,
  step,
  placeholder,
  className,
  id,
  style,
}: {
  value?: number | null;
  onValueChange?: (value: number | null) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  className?: string;
  id?: string;
  style?: React.CSSProperties;
}) {
  const generatedId = useId();

  return (
    <NumberFieldPrimitive.Root
      id={id ?? generatedId}
      value={value ?? null}
      onValueChange={(next) => onValueChange?.(next)}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      className={cn("w-full", className)}
      style={style}
    >
      <NumberFieldPrimitive.Group className="flex h-9 w-full items-center rounded-full border border-input bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30 has-disabled:opacity-50">
        <NumberFieldPrimitive.Decrement
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none"
          aria-label="Decrease"
        >
          <IconMinus size={14} />
        </NumberFieldPrimitive.Decrement>
        <NumberFieldPrimitive.Input
          placeholder={placeholder}
          className="w-full min-w-0 bg-transparent text-center text-sm outline-none"
        />
        <NumberFieldPrimitive.Increment
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none"
          aria-label="Increase"
        >
          <IconPlus size={14} />
        </NumberFieldPrimitive.Increment>
      </NumberFieldPrimitive.Group>
    </NumberFieldPrimitive.Root>
  );
}

type NativeFieldProps = Omit<ComponentProps<typeof Input>, "type">;

/** antd `<DatePicker>`. `withTime` switches to `datetime-local`. */
export function DateField({
  withTime,
  granularity = "day",
  className,
  ...props
}: NativeFieldProps & {
  withTime?: boolean;
  /** antd's `picker`. Maps onto the native input types that exist. */
  granularity?: "day" | "week" | "month";
}) {
  const type = withTime
    ? "datetime-local"
    : granularity === "week"
      ? "week"
      : granularity === "month"
        ? "month"
        : "date";
  return <Input type={type} className={cn("w-full", className)} {...props} />;
}

/** antd `<TimePicker>`. */
export function TimeField({ className, ...props }: NativeFieldProps) {
  return <Input type="time" className={cn("w-full", className)} {...props} />;
}

/** antd `<ColorPicker showText format="hex">`. */
export function ColorField({
  value,
  defaultValue = "#1677ff",
  onChange,
  disabled,
  className,
  name,
}: {
  value?: string;
  defaultValue?: string;
  onChange?: (hex: string) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
}) {
  const [internal, setInternal] = useState(defaultValue);
  const hex = value ?? internal;

  return (
    <div
      className={cn(
        "flex h-9 w-full items-center gap-2 rounded-full border border-input px-1.5 pr-3 shadow-xs focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <input
        type="color"
        name={name}
        value={hex}
        disabled={disabled}
        onChange={(event) => {
          setInternal(event.target.value);
          onChange?.(event.target.value);
        }}
        className="size-6 shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
        aria-label="Pick a colour"
      />
      <span className="font-mono text-sm text-muted-foreground uppercase">
        {hex}
      </span>
    </div>
  );
}

/** antd `<Upload.Dragger>`. */
export function FileDropzone({
  onFiles,
  accept,
  multiple,
  disabled,
  hint,
  className,
}: {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const take = (list: FileList | null) => {
    if (!list?.length) return;
    onFiles(Array.from(list));
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => inputRef.current?.click()}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        take(event.dataTransfer.files);
      }}
      className={cn(
        "group flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 transition-all",
        dragging
          ? "scale-[1.01] border-primary bg-primary/10"
          : "border-border hover:border-primary hover:bg-primary/5",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) => take(event.target.files)}
        className="hidden"
      />
      {hint ? (
        hint
      ) : (
        <>
          <IconUpload
            size={28}
            className={cn(
              "transition-colors",
              dragging
                ? "text-primary"
                : "text-muted-foreground group-hover:text-primary",
            )}
          />
          <span className="text-sm font-medium text-foreground">
            Drop files here, or{" "}
            <span className="text-primary underline">browse</span>
          </span>
        </>
      )}
    </button>
  );
}

/** A removable chip for a picked file, to sit under a `<FileDropzone>`. */
export function FileChip({
  name,
  onRemove,
  disabled,
}: {
  name: string;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-medium",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <span className="max-w-[180px] truncate">{name}</span>
      {onRemove && !disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="rounded-full"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
        >
          <IconX size={12} />
        </Button>
      ) : null}
    </span>
  );
}
